import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useEffect, useMemo, useRef } from 'react';
import type { DeltaStatic } from 'quill';

type Props = {
    text: string;
    setText: (text: string) => void;
    bgColor?: string;
};

const supportedFontSizes: string[] = [];

for (let size = 10; size <= 32; size += 2) {
    supportedFontSizes.push(size + 'px');
}

const supportedFontsList: string[] = [
    'Black Ops One',
    'Iceberg',
    'UnifrakturMaguntia',
    'Barriecito',
    'Protest Guerrilla',
    'Lato',
    'Nosifer',
    'Roboto',
    'Google Sans Code',
    'Henny Penny',
];

const SizeStyle = Quill.import('attributors/style/size');
const FontStyle = Quill.import('attributors/style/font');
const alignStyle = Quill.import('attributors/style/align');

SizeStyle.whitelist = supportedFontSizes;
FontStyle.whitelist = supportedFontsList;

Quill.register(SizeStyle, true);
Quill.register(FontStyle, true);
Quill.register(alignStyle, true);

function getColorSelector(className: string, count: number): React.ReactElement {
    const colors = [];

    for (let i = 0; i < count; i++) {
        const hue = Math.round((360 / count) * i); // evenly spaced hues
        colors.push(hslToHex(hue, 80, 50)); // adjustable S & L
    }

    return (
        <select className={className}>
            <option selected></option>
            {colors.map(color => (
                <option key={color} value={color} style={{ backgroundColor: color }}>
                    {color}
                </option>
            ))}
            <option value="#ffffffff">#ffffffff</option>
        </select>
    );
}

function hslToHex(h: number, s: number, l: number) {
    s /= 100;
    l /= 100;

    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

    const toHex = (x: number) => {
        const h = Math.round(255 * x).toString(16);
        return h.length === 1 ? '0' + h : h;
    };

    return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

function getFontSizeSelector(className: string): React.ReactElement {
    return (
        <select className={className}>
            <option selected></option>
            {supportedFontSizes.map(size => (
                <option key={size} value={size}>
                    {size}
                </option>
            ))}
        </select>
    );
}

function getFontSelector(className: string): React.ReactElement {
    return (
        <select className={className}>
            <option selected></option>
            {supportedFontsList.map(font => (
                <option key={font} value={font}>
                    {font}
                </option>
            ))}
        </select>
    );
}

function getAlignSelector(className: string): React.ReactElement {
    return (
        <select className={className}>
            <option selected></option>
            <option value="right">Right</option>
            <option value="center">Center</option>
            <option value="justify">Justify</option>
        </select>
    );
}

export function RichTextComponent({ text, setText, bgColor = '#f0f0f0' }: Readonly<Props>) {
    const quillRef = useRef<ReactQuill>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const id = useMemo(() => Math.random().toString(36).substring(2, 15), []);
    // const isFixing = useRef(false);

    useEffect(() => {
        if (toolbarRef.current) {
            // Set font family for font picker items
            toolbarRef.current.querySelectorAll('.ql-font .ql-picker-item').forEach(item => {
                const htmlElement = item as HTMLElement;
                htmlElement.style.fontFamily = htmlElement.dataset['value'] || 'inherit';
            });

            // Adjust dropdown position to stay within window bounds
            const optionsElements = toolbarRef.current.querySelectorAll('.ql-picker-options');

            if (optionsElements.length === 0) {
                console.error('[mv-cd]: No options elements found');
                return;
            }

            const observer = new MutationObserver(mutations => {
                for (const mutation of mutations) {
                    if (mutation.type !== 'attributes') {
                        continue;
                    }

                    const element = mutation.target as HTMLElement;
                    const value = element.getAttribute('aria-hidden');

                    if (value !== 'false') {
                        continue;
                    }

                    const { right: parentRight } = document.body.getBoundingClientRect();
                    const { right: elementRight } = element.getBoundingClientRect();

                    if (elementRight > parentRight) {
                        const adjustedLeft = Math.max(parentRight - elementRight - 20, -10000);
                        element.style.left = `${adjustedLeft - 10}px`;
                    }
                }
            });

            optionsElements.forEach(optionsElement => {
                observer.observe(optionsElement, {
                    attributes: true,
                    attributeFilter: ['aria-hidden'],
                });
            });
            return () => observer.disconnect();
        }
    }, [toolbarRef.current]);

    const onTextChange = useMemo(() => {
        return (delta: DeltaStatic, oldDelta: DeltaStatic, source: string) => {
            if (source !== 'user') return;
            if (quillRef.current === null) return;
            // @ts-ignore
            // quillRef.current?.getEditor().history.undo();
            const editor = quillRef.current.getEditor();
            const doc = editor.getContents(); // FULL delta
            let changed = false;

            // @ts-ignore
            doc.ops = doc.ops.map(op => {
                if (typeof op.insert === 'string') {
                    const fixed = op.insert.replaceAll(/ {2,}/g, m => ' ' + '\u00A0'.repeat(m.length - 1));
                    if (fixed !== op.insert) {
                        changed = true;
                        return { ...op, insert: fixed };
                    }
                }
                return op;
            });

            if (changed) {
                const range = editor.getSelection();
                editor.setContents(doc, 'silent');
                if (range) {
                    setTimeout(() => {
                        editor.setSelection(range, 'silent');
                    }, 0);
                }
            }
        };
    }, [setText]);

    useEffect(() => {
        if (!quillRef.current) return;
        quillRef.current.getEditor().on('text-change', onTextChange);

        return () => {
            if (!quillRef.current) return;
            quillRef.current.getEditor().off('text-change', onTextChange);
        };
    }, [quillRef.current]);

    useEffect(() => {
        if (!quillRef.current) return;
        const editor = quillRef.current.getEditor();
        const editorElement = editor.root;
        editorElement.style.backgroundColor = bgColor;
    }, [bgColor]);

    // console.log(text);
    return (
        <div>
            <div id={`toolbar-container-${id}`} style={{ zIndex: 25, position: 'relative' }} ref={toolbarRef}>
                <span className="ql-formats">
                    <button className="ql-bold">Bold</button>
                    <button className="ql-italic">Italic</button>
                    <button className="ql-underline">Underline</button>
                    <button className="ql-strike">Strike</button>
                    {getFontSizeSelector('ql-size')}
                    {getColorSelector('ql-color', 142)}
                    {getColorSelector('ql-background', 142)}
                    {getAlignSelector('ql-align')}
                    {getFontSelector('ql-font')}
                    <button className="ql-clean">Clear</button>
                </span>
            </div>
            <ReactQuill
                theme="snow"
                value={text}
                onChange={text => {
                    setText(text);
                    console.log(text);
                }}
                modules={{
                    toolbar: `#toolbar-container-${id}`,
                    history: {
                        delay: 0,
                        maxStack: 100,
                        userOnly: true,
                    },
                }}
                ref={quillRef}
            />
        </div>
    );
}
